import { forwardRef, type ComponentProps } from 'react';
import ChevronDownIcon from '@/shared/assets/header/chev-down.svg?react';
import { DeltaLabel } from '@/shared/components';
import { HIDDEN_BALANCES_FULL_TEXT } from '@/shared/config';
import { formatBalance } from '@/shared/lib';
import { cn } from '@/shared/utils';
import type { GeneralBalanceData } from '@/entities/account';
import { UserAvatar } from './UserAvatar';

type Props = ComponentProps<'button'> & {
  general: GeneralBalanceData;
  initial: string;
  shortName: string;
  isCorporate: boolean;
  isVerified: boolean;
  isBalancesHidden: boolean;
  showName: boolean;
  isOpen: boolean;
};

export const UserBalanceTrigger = forwardRef<HTMLButtonElement, Props>(
  (
    {
      general,
      initial,
      shortName,
      isCorporate,
      isVerified,
      isBalancesHidden,
      showName,
      isOpen,
      className,
      ...props
    },
    ref
  ) => {
    const balanceText = isBalancesHidden
      ? HIDDEN_BALANCES_FULL_TEXT
      : formatBalance(general.balance, general.basePrecission);

    return (
      <button
        ref={ref}
        type="button"
        className={cn(
          'flex items-center gap-[0.8rem] h-[3.2rem] px-[0.8rem] py-[0.6rem] rounded-[1rem] border border-stroke-bg bg-background text-primary cursor-pointer max-w-full',
          'hover:bg-stroke data-[state=open]:bg-secondary-bg transition-colors',
          className
        )}
        {...props}>
        <UserAvatar
          initial={initial}
          isCorporate={isCorporate}
          isVerified={isVerified}
        />

        <span className="headline5 flex items-center gap-[0.4rem] min-w-0">
          {showName && <span className="truncate">{shortName}:</span>}
          <span className="whitespace-nowrap">
            {balanceText} {general.baseCode}
          </span>
        </span>

        <DeltaLabel
          deltaPercent={general.deltaPercent}
          size="card"
          showLabelText={false}
          className="px-0 py-[0.2rem]"
          deltaPositiveClassName="bg-transparent text-primary-cyan"
          deltaNegativeClassName="bg-transparent text-red"
          arrowPositiveClassName="text-primary-cyan"
        />

        <ChevronDownIcon
          className={cn(
            'size-[1.6rem] shrink-0 text-primary transition-transform',
            isOpen && 'rotate-180'
          )}
        />
      </button>
    );
  }
);

UserBalanceTrigger.displayName = 'UserBalanceTrigger';
